import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import '../App.css';
import api from '../api';
import { Sport, ArbitrageOpportunity } from '../types';
import OpportunityTable from '../components/OpportunityTable';
import FiltersPanel from '../components/FiltersPanel';
import InlineFilters from '../components/InlineFilters';
import EVOpportunities from '../components/EVOpportunities';
import { DEFAULT_US_BOOKMAKERS } from '../bookmakers';
import { useAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';
import { formatResetCountdown } from '../utils/time';

export default function DashboardPage() {
  const { profile, profileLoading, refreshProfile, user, session, profileError: contextProfileError } = useAuth();
  const hasInitiallyRefreshed = useRef(false);

  // Don't default to 'free' until we know the profile has loaded
  const userTier = useMemo(() => {
    // If we have a profile, use its tier
    if (profile) {
      return profile.subscription_tier;
    }

    // If profile is loading, check for cached profile to avoid showing 'free' flash
    const cachedProfile = sessionStorage.getItem('cached_profile');
    if (cachedProfile) {
      try {
        const parsed = JSON.parse(cachedProfile);
        if (parsed?.subscription_tier) {
          return parsed.subscription_tier;
        }
      } catch {
        // Ignore parse errors
      }
    }

    // If profile is still loading (or we're waiting for initial auth), return null
    // This prevents defaulting to 'free' prematurely
    // If we have no profile and are logged in, we should show loading until we get one (or timeout)
    return null;
  }, [profile]);

  // Handle profile loading timeout
  const [profileLoadError, setProfileLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (userTier === null) {
      const timer = setTimeout(() => {
        setProfileLoadError('Unable to determine subscription status. Please refresh the page.');
      }, 10000); // 10 seconds timeout
      return () => clearTimeout(timer);
    }
    setProfileLoadError(null);
  }, [userTier]);

  type LoadOptions = { force?: boolean };
  const previousProfileRef = useRef<typeof profile>(null);
  const sportsLoadedRef = useRef(false);
  const hasRefreshedProfileOnMount = useRef(false);
  const [activeTab, setActiveTab] = useState<'game-arbitrage' | 'ev'>('game-arbitrage');
  const [evHasManuallySearched, setEvHasManuallySearched] = useState(false);
  const [sports, setSports] = useState<Sport[]>([]);
  const [opportunities, setOpportunities] = useState<ArbitrageOpportunity[]>([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState<ArbitrageOpportunity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [filtersPanelMode, setFiltersPanelMode] = useState<'game-arbitrage' | 'ev'>('game-arbitrage');
  const [hasManuallySearched, setHasManuallySearched] = useState(false);
  const [oddsFormat, setOddsFormat] = useState<'decimal' | 'american'>('american');
  const inFlightQueryRef = useRef<string | null>(null);
  const lastQuerySignatureRef = useRef<string | null>(null);
  const opportunitiesHashRef = useRef<string>('');

  // Auto-refresh for Platinum users
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(10);
  const [showAutoRefreshUpgrade, setShowAutoRefreshUpgrade] = useState(false);
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoRefreshDropdownRef = useRef<HTMLDivElement>(null);

  // Bet calculator states
  const [maxBetSize, setMaxBetSize] = useState<number>(100);
  const [roundBets, setRoundBets] = useState<boolean>(true);

  // Filter states (for arbitrage)
  const [selectedSports, setSelectedSports] = useState<string[]>(['upcoming']);
  const [selectedRegions, setSelectedRegions] = useState<string[]>(['us']);
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>(['h2h']);
  const [selectedBookmakers, setSelectedBookmakers] = useState<string[]>([...DEFAULT_US_BOOKMAKERS]);
  const [selectedGameStatus, setSelectedGameStatus] = useState<string[]>(['live', 'upcoming']);
  const [selectedPlayerPropMarkets, setSelectedPlayerPropMarkets] = useState<string[]>([]);
  const [minProfit, setMinProfit] = useState(0.5);
  const [maxProfit, setMaxProfit] = useState(100);
  const [searchTerm] = useState('');

  const [draftSelectedSports, setDraftSelectedSports] = useState<string[]>(['upcoming']);
  const [draftSelectedRegions, setDraftSelectedRegions] = useState<string[]>(['us']);
  const [draftSelectedMarkets, setDraftSelectedMarkets] = useState<string[]>(['h2h']);
  const [draftSelectedBookmakers, setDraftSelectedBookmakers] = useState<string[]>([...DEFAULT_US_BOOKMAKERS]);
  const [draftSelectedGameStatus, setDraftSelectedGameStatus] = useState<string[]>(['live', 'upcoming']);
  const [draftSelectedPlayerPropMarkets, setDraftSelectedPlayerPropMarkets] = useState<string[]>([]);
  const [draftMinProfit, setDraftMinProfit] = useState(0.5);
  const [draftMaxProfit, setDraftMaxProfit] = useState(100);

  // Filter states (for +EV)
  const [evSelectedSports, setEvSelectedSports] = useState<string[]>(['upcoming']);
  const [evSelectedMarkets, setEvSelectedMarkets] = useState<string[]>(['h2h']);
  const [evSelectedBookmakers, setEvSelectedBookmakers] = useState<string[]>([]);
  const [evSelectedPlayerPropMarkets, setEvSelectedPlayerPropMarkets] = useState<string[]>([]);
  const [evMinEV, setEvMinEV] = useState(1.0);
  const [evMaxEV, setEvMaxEV] = useState(100);
  const [evMinProbability, setEvMinProbability] = useState(0);
  const [evMaxProbability, setEvMaxProbability] = useState(100);
  const [evSearchTerm] = useState('');
  const [evSelectedGameStatus, setEvSelectedGameStatus] = useState<string[]>(['live', 'upcoming']);
  const [draftEvSelectedSports, setDraftEvSelectedSports] = useState<string[]>(['upcoming']);
  const [draftEvSelectedMarkets, setDraftEvSelectedMarkets] = useState<string[]>(['h2h']);
  const [draftEvSelectedBookmakers, setDraftEvSelectedBookmakers] = useState<string[]>([]);
  const [draftEvSelectedPlayerPropMarkets, setDraftEvSelectedPlayerPropMarkets] = useState<string[]>([]);
  const [draftEvMinEV, setDraftEvMinEV] = useState(1.0);
  const [draftEvMaxEV, setDraftEvMaxEV] = useState(100);
  const [draftEvMinProbability, setDraftEvMinProbability] = useState(0);
  const [draftEvMaxProbability, setDraftEvMaxProbability] = useState(100);
  const [draftEvSelectedGameStatus, setDraftEvSelectedGameStatus] = useState<string[]>(['live', 'upcoming']);

  useEffect(() => {
    if (userTier !== 'free') {
      return;
    }

    const ensureLiveDisabled = (statuses: string[]) => {
      const filtered = statuses.filter(status => status !== 'live');
      return filtered.length > 0 ? filtered : ['upcoming'];
    };

    setSelectedGameStatus(current => ensureLiveDisabled(current));
    setDraftSelectedGameStatus(current => ensureLiveDisabled(current));
    setEvSelectedGameStatus(current => ensureLiveDisabled(current));
    setDraftEvSelectedGameStatus(current => ensureLiveDisabled(current));
  }, [userTier]);

  useEffect(() => {
    if (userTier && userTier !== 'platinum') {
      setSelectedPlayerPropMarkets([]);
      setDraftSelectedPlayerPropMarkets([]);
    }
  }, [userTier]);

  const remainingCredits = profile?.api_calls_remaining;
  const hasCreditData = typeof remainingCredits === 'number';
  const isOutOfCredits = hasCreditData && remainingCredits <= 0;
  const nextResetMessage = useMemo(
    () => formatResetCountdown(profile?.api_calls_reset_at),
    [profile?.api_calls_reset_at]
  );
  const refreshCredits = useCallback(async () => {
    try {
      console.log('[refreshCredits] Starting profile refresh...');
      await refreshProfile({ force: true, silent: true });
      console.log('[refreshCredits] Profile refresh completed');
    } catch (error) {
      console.error('[refreshCredits] Error refreshing profile credits:', error);
    }
  }, [refreshProfile]);

  // Debug: Log tier changes
  useEffect(() => {
    console.log('[Dashboard] User tier:', userTier, 'Profile:', profile, 'ProfileLoading:', profileLoading);
    if (profile) {
      console.log('[Dashboard] Profile details:', {
        id: profile.id,
        email: profile.email,
        subscription_tier: profile.subscription_tier,
        subscription_status: profile.subscription_status,
      });
    }
  }, [userTier, profile, profileLoading]);

  // Check if profile might be stale
  const [isProfileStale, setIsProfileStale] = useState(false);

  useEffect(() => {
    const cachedTime = sessionStorage.getItem('cached_profile_time');
    if (!cachedTime) {
      setIsProfileStale(false);
      return;
    }

    const age = Date.now() - parseInt(cachedTime);
    // Consider profile stale if older than 2 minutes
    setIsProfileStale(age > 120000);
  }, [profile]);

  // Keep loading ref in sync
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // Force refresh profile on mount to ensure latest tier data
  useEffect(() => {
    const forceRefresh = async () => {
      if (session && user && !hasRefreshedProfileOnMount.current) {
        hasRefreshedProfileOnMount.current = true;
        console.log('[Dashboard] Forcing profile refresh on mount...', {
          hasProfile: !!profile,
          profileLoading,
          sessionId: session.user.id
        });
        try {
          await refreshProfile({ force: true, silent: false });
          console.log('[Dashboard] Profile refresh on mount completed');
        } catch (error) {
          console.error('[Dashboard] Profile refresh on mount failed:', error);
        }
      }
    };
    forceRefresh();
  }, [session, user, refreshProfile]);

  // Load sports on mount (only after auth is ready)
  useEffect(() => {
    if (session && user && !sportsLoadedRef.current) {
      sportsLoadedRef.current = true;
      loadSports();
    }
  }, [session, user]);

  // Refresh profile when user returns to the page (e.g., from billing)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && session && user) {
        console.log('[Dashboard] Page visible, refreshing profile...', {
          currentTier: userTier,
          hasProfile: !!profile
        });
        try {
          await refreshProfile({ force: true, silent: false });
          console.log('[Dashboard] Profile refreshed on visibility change');
        } catch (error) {
          console.error('[Dashboard] Failed to refresh profile on visibility change:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshProfile, session, user, userTier, profile]);

  // Fetch API usage stats on mount and periodically
  useEffect(() => {
    // Don't try to refresh if profile is still loading from auth initialization
    // This prevents race conditions with the initial profile fetch
    if (profileLoading) {
      return;
    }

    const hasCredits = typeof profile?.api_calls_remaining === 'number';
    const profileJustLoaded = previousProfileRef.current === null && profile !== null;

    // Refresh credits if:
    // 1. First time mounting and no credits available, OR
    // 2. Profile just transitioned from null to loaded and we still don't have credits
    if (!hasCredits && (!hasInitiallyRefreshed.current || profileJustLoaded)) {
      refreshCredits();
      hasInitiallyRefreshed.current = true;
    } else if (hasCredits && !hasInitiallyRefreshed.current) {
      hasInitiallyRefreshed.current = true;
    }

    // Update previous profile ref for next render
    previousProfileRef.current = profile;

    // Continue periodic refresh every 30 seconds
    const interval = setInterval(() => {
      refreshCredits();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [profile, profileLoading, refreshCredits]);

  // Filter opportunities based on search term and game status
  useEffect(() => {
    let filtered = opportunities;

    // Filter by game status (live/upcoming)
    const now = new Date();
    filtered = filtered.filter(opp => {
      const commenceTime = new Date(opp.commence_time);
      const isLive = commenceTime < now;

      if (selectedGameStatus.includes('live') && selectedGameStatus.includes('upcoming')) {
        return true; // Show all
      } else if (selectedGameStatus.includes('live')) {
        return isLive;
      } else if (selectedGameStatus.includes('upcoming')) {
        return !isLive;
      }
      return false;
    });

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(opp =>
        opp.home_team.toLowerCase().includes(term) ||
        opp.away_team.toLowerCase().includes(term) ||
        opp.sport_title?.toLowerCase().includes(term) ||
        opp.sport_key.toLowerCase().includes(term)
      );
    }

    setFilteredOpportunities(filtered);
  }, [searchTerm, opportunities, selectedGameStatus]);

  const loadSports = async () => {
    try {
      const response = await api.getSports(true);
      setSports(response.data);
    } catch (err) {
      console.error('Error loading sports:', err);
    }
  };

  const getArbitrageQuerySignature = useCallback(() => {
    const sortedSports = [...selectedSports].sort();
    const sortedRegions = [...selectedRegions].sort();
    const sortedMarkets = [...selectedMarkets].sort();
    const sortedBookmakers = [...selectedBookmakers].sort();
    const sortedPlayerPropMarkets = userTier === 'platinum'
      ? [...selectedPlayerPropMarkets].sort()
      : [];
    const includesLiveGames = selectedGameStatus.includes('live');

    return JSON.stringify({
      sports: sortedSports,
      regions: sortedRegions,
      markets: sortedMarkets,
      playerPropMarkets: sortedPlayerPropMarkets,
      bookmakers: sortedBookmakers,
      minProfit,
      maxProfit,
      includesLiveGames,
    });
  }, [
    selectedSports,
    selectedRegions,
    selectedMarkets,
    selectedBookmakers,
    selectedPlayerPropMarkets,
    selectedGameStatus,
    minProfit,
    maxProfit,
    userTier,
  ]);

  const loadOpportunities = useCallback(async ({ force = false }: LoadOptions = {}) => {
    const querySignature = getArbitrageQuerySignature();

    // Mark that user has manually searched
    if (force) {
      setHasManuallySearched(true);
    }

    if (!force) {
      if (inFlightQueryRef.current === querySignature) {
        return;
      }

      if (lastQuerySignatureRef.current === querySignature) {
        return;
      }
    }

    inFlightQueryRef.current = querySignature;
    console.log('[loadOpportunities] Setting loading to true');
    setLoading(true);
    setError(null);

    try {
      console.log('[loadOpportunities] Fetching opportunities...');

      const canRequestPlayerProps = userTier === 'platinum';
      const playerPropMarkets = canRequestPlayerProps ? selectedPlayerPropMarkets : [];
      const markets = selectedMarkets.length > 0
        ? selectedMarkets
        : playerPropMarkets.length > 0 ? ['player_props'] : ['h2h'];

      const response = await api.getArbitrageOpportunities({
        sports: selectedSports,
        regions: selectedRegions,
        markets,
        oddsFormat: 'decimal', // API always returns decimal, we convert for display
        minProfit,
        bookmakers: selectedBookmakers.length > 0 ? selectedBookmakers : undefined,
        includeLiveGames: selectedGameStatus.includes('live'),
        playerPropMarkets: playerPropMarkets.length > 0 ? playerPropMarkets : undefined,
      });

      console.log('[loadOpportunities] Received response with', response.data.length, 'opportunities');

      // Filter by maxProfit on the client side
      const filteredByMaxProfit = response.data.filter(
        opp => opp.profit_percentage <= maxProfit
      );

      // Only update if data has changed (compare by stringifying)
      const newDataStr = JSON.stringify(filteredByMaxProfit);
      const currentDataStr = opportunitiesHashRef.current;

      // Only update if data has changed (compare by stringifying)

      if (newDataStr !== currentDataStr) {
        opportunitiesHashRef.current = newDataStr;
        setOpportunities(filteredByMaxProfit);
        setFilteredOpportunities(filteredByMaxProfit);
      }

      lastQuerySignatureRef.current = querySignature;

      console.log('[loadOpportunities] Refreshing credits...');
      // Update profile credits immediately after fetching (non-blocking)
      refreshCredits().catch(err => {
        console.error('[loadOpportunities] Failed to refresh credits:', err);
      });
      console.log('[loadOpportunities] Credits refresh triggered (non-blocking)');
    } catch (err) {
      console.error('[loadOpportunities] Error:', err);
      const message = err instanceof Error ? err.message : 'Failed to load opportunities';
      setError(message);
    } finally {
      console.log('[loadOpportunities] Setting loading to false');
      inFlightQueryRef.current = null;
      setLoading(false);
    }
  }, [
    getArbitrageQuerySignature,
    maxProfit,
    minProfit,
    refreshCredits,
    selectedBookmakers,
    selectedGameStatus,
    selectedMarkets,
    selectedPlayerPropMarkets,
    selectedRegions,
    selectedSports,
    userTier,
  ]);

  // Don't auto-load opportunities on mount - require manual search
  // This prevents using credits immediately when loading the dashboard
  useEffect(() => {
    // Only auto-load if user has already done a manual search
    // Don't include loadOpportunities in deps to avoid infinite loop
    if (hasManuallySearched) {
      loadOpportunities();
    }
  }, [
    hasManuallySearched,
    // Only re-run when query parameters actually change
    selectedSports,
    selectedRegions,
    selectedMarkets,
    selectedBookmakers,
    selectedGameStatus,
    minProfit,
    maxProfit,
    oddsFormat,
    loadOpportunities,
  ]);

  // Close auto-refresh dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autoRefreshDropdownRef.current && !autoRefreshDropdownRef.current.contains(event.target as Node)) {
        setShowAutoRefreshUpgrade(false);
      }
    };

    if (showAutoRefreshUpgrade) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAutoRefreshUpgrade]);

  // Auto-refresh for Platinum users
  useEffect(() => {
    // Clear any existing intervals
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    // Only enable auto-refresh for platinum users who have searched and enabled it
    if (userTier === 'platinum' && autoRefreshEnabled && hasManuallySearched && activeTab === 'game-arbitrage' && !loading) {
      // Reset countdown
      setSecondsUntilRefresh(10);

      // Set up countdown timer (updates every second)
      countdownIntervalRef.current = setInterval(() => {
        setSecondsUntilRefresh((prev) => {
          if (prev <= 1) {
            return 10; // Reset to 10 when it hits 0
          }
          return prev - 1;
        });
      }, 1000);

      // Set up refresh interval (every 10 seconds)
      autoRefreshIntervalRef.current = setInterval(() => {
        // Only trigger if not already loading (use ref to get current value)
        if (!loadingRef.current) {
          console.log('[Auto-refresh] Refreshing opportunities...');
          loadOpportunities({ force: true });
        } else {
          console.log('[Auto-refresh] Skipping refresh, already loading...');
        }
      }, 10000);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [userTier, autoRefreshEnabled, hasManuallySearched, activeTab, loading, loadOpportunities]);

  const openFiltersPanel = (mode: 'game-arbitrage' | 'ev') => {
    if (mode === 'game-arbitrage') {
      setDraftSelectedSports([...selectedSports]);
      setDraftSelectedRegions([...selectedRegions]);
      setDraftSelectedMarkets([...selectedMarkets]);
      setDraftSelectedBookmakers([...selectedBookmakers]);
      setDraftSelectedGameStatus([...selectedGameStatus]);
      setDraftSelectedPlayerPropMarkets([...selectedPlayerPropMarkets]);
      setDraftMinProfit(minProfit);
      setDraftMaxProfit(maxProfit);
    } else {
      setDraftEvSelectedSports([...evSelectedSports]);
      setDraftEvSelectedMarkets([...evSelectedMarkets]);
      setDraftEvSelectedBookmakers([...evSelectedBookmakers]);
      setDraftEvSelectedPlayerPropMarkets([...evSelectedPlayerPropMarkets]);
      setDraftEvMinEV(evMinEV);
      setDraftEvMaxEV(evMaxEV);
      setDraftEvMinProbability(evMinProbability);
      setDraftEvMaxProbability(evMaxProbability);
      setDraftEvSelectedGameStatus([...evSelectedGameStatus]);
    }

    setFiltersPanelMode(mode);
    setFiltersPanelOpen(true);
  };

  const handleApplyFilters = () => {
    if (filtersPanelMode === 'game-arbitrage') {
      setSelectedSports([...draftSelectedSports]);
      setSelectedRegions([...draftSelectedRegions]);
      const nextPlayerPropMarkets = userTier === 'platinum'
        ? [...draftSelectedPlayerPropMarkets]
        : [];
      const nextMarkets = draftSelectedMarkets.length > 0
        ? [...draftSelectedMarkets]
        : [];
      if (!nextMarkets.includes('h2h') && nextPlayerPropMarkets.length === 0) {
        nextMarkets.push('h2h');
      }
      setSelectedMarkets(nextMarkets);
      setSelectedBookmakers([...draftSelectedBookmakers]);
      setSelectedGameStatus([...draftSelectedGameStatus]);
      setSelectedPlayerPropMarkets(nextPlayerPropMarkets);
      setMinProfit(draftMinProfit);
      setMaxProfit(draftMaxProfit);
    } else {
      setEvSelectedSports([...draftEvSelectedSports]);
      setEvSelectedMarkets([...draftEvSelectedMarkets]);
      setEvSelectedBookmakers([...draftEvSelectedBookmakers]);
      setEvSelectedPlayerPropMarkets([...draftEvSelectedPlayerPropMarkets]);
      setEvMinEV(draftEvMinEV);
      setEvMaxEV(draftEvMaxEV);
      setEvMinProbability(draftEvMinProbability);
      setEvMaxProbability(draftEvMaxProbability);
      setEvSelectedGameStatus([...draftEvSelectedGameStatus]);
    }

    setFiltersPanelOpen(false);
  };

  const handleCancelFilters = () => {
    setFiltersPanelOpen(false);
  };

  const handleManualRefresh = () => {
    // Don't trigger if already loading
    if (loadingRef.current) {
      console.log('[Manual refresh] Already loading, skipping...');
      return;
    }
    loadOpportunities({ force: true });
  };

  // Calculate bet amounts for arbitrage opportunity
  // totalBankroll is the total amount to bet across all outcomes
  const calculateBetAmounts = (opportunity: ArbitrageOpportunity, totalBankroll: number, shouldRound: boolean) => {
    const odds = opportunity.best_odds.map(bet => bet.odds);

    // Calculate sum of inverse odds (total implied probability)
    const sumInverseOdds = odds.reduce((sum, odd) => sum + (1 / odd), 0);

    // Calculate stake percentages for each outcome
    const stakePercentages = odds.map(odd => (1 / odd) / sumInverseOdds);

    // Helper function to determine rounding value based on bet amount
    const getRoundingValue = (amount: number): number => {
      if (amount < 100) return 1;
      if (amount < 500) return 5;
      if (amount < 1000) return 10;
      if (amount < 2500) return 25;
      if (amount < 5000) return 50;
      return 100;
    };

    // Calculate individual bet amounts with graduated rounding
    const betAmounts = stakePercentages.map(stakePct => {
      const amount = totalBankroll * stakePct;
      if (!shouldRound) {
        return amount; // No rounding
      }
      const roundingValue = getRoundingValue(amount);
      return Math.round(amount / roundingValue) * roundingValue;
    });

    // Calculate profit based on actual rounded bet amounts
    // For each outcome, calculate what profit would be if that outcome wins
    // Use the minimum to be conservative (due to rounding discrepancies)
    const totalBet = betAmounts.reduce((sum, amount) => sum + amount, 0);
    const profits = betAmounts.map((amount, idx) => {
      const payout = amount * odds[idx];
      return payout - totalBet;
    });
    const profit = Math.min(...profits);

    return { betAmounts, profit };
  };

  const getActiveFiltersText = () => {
    const parts: string[] = [];

    if (selectedSports.includes('upcoming')) {
      parts.push('All Sports');
    } else {
      const sportNames = selectedSports.map(key =>
        sports.find(s => s.key === key)?.title || key
      );
      parts.push(sportNames.join(', '));
    }

    if (selectedBookmakers.length > 0) {
      parts.push(`${selectedBookmakers.length} Bookmaker${selectedBookmakers.length > 1 ? 's' : ''}`);
    } else {
      parts.push(selectedRegions.map(r => r.toUpperCase()).join(', '));
    }

    const marketLabels: Record<string, string> = {
      h2h: 'Moneyline',
      spreads: 'Spreads',
      totals: 'Totals',
    };
    const selectedMarketLabels = selectedMarkets.map(market => marketLabels[market] || market);
    if (selectedPlayerPropMarkets.length > 0) {
      selectedMarketLabels.push(
        `${selectedPlayerPropMarkets.length} Prop${selectedPlayerPropMarkets.length === 1 ? '' : 's'}`
      );
    }
    if (selectedMarketLabels.length > 0) {
      parts.push(selectedMarketLabels.join(' + '));
    }

    parts.push(`${minProfit}%-${maxProfit}% profit`);

    const statusParts = [];
    if (selectedGameStatus.includes('live')) statusParts.push('Live');
    if (selectedGameStatus.includes('upcoming')) statusParts.push('Upcoming');
    parts.push(statusParts.join(' & '));

    return parts.join(' • ');
  };

  // Calculate stats
  return (
    <div className="app">
      {/* Desktop Mode Switcher - Top of page */}
      <div className="desktop-mode-switcher">
        <div className="mode-switcher-container">
          <div className="mode-switcher-tabs">
            <button
              className={`mode-tab ${activeTab === 'game-arbitrage' ? 'active' : ''}`}
              onClick={() => setActiveTab('game-arbitrage')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              Arbitrage
            </button>
            <button
              className={`mode-tab ${activeTab === 'ev' ? 'active' : ''}`}
              onClick={() => setActiveTab('ev')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                <polyline points="17 6 23 6 23 12"></polyline>
              </svg>
              +EV
            </button>
          </div>
        </div>
      </div>

      <div className="main-layout">
        {/* Mobile Bottom Navigation */}
        <nav className="mobile-bottom-nav">
          <button
            className={`bottom-nav-item ${activeTab === 'game-arbitrage' ? 'active' : ''}`}
            onClick={() => setActiveTab('game-arbitrage')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
            <span>Arbitrage</span>
          </button>
          <button
            className={`bottom-nav-item ${activeTab === 'ev' ? 'active' : ''}`}
            onClick={() => setActiveTab('ev')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
              <polyline points="17 6 23 6 23 12"></polyline>
            </svg>
            <span>+EV</span>
          </button>
        </nav>

        {/* Filters Panel */}
        {activeTab === 'game-arbitrage' && (
          <FiltersPanel
            isOpen={filtersPanelOpen && filtersPanelMode === 'game-arbitrage'}
            onCancel={handleCancelFilters}
            onApply={handleApplyFilters}
            sports={sports}
            selectedSports={draftSelectedSports}
            setSelectedSports={setDraftSelectedSports}
            selectedRegions={draftSelectedRegions}
            setSelectedRegions={setDraftSelectedRegions}
            selectedMarkets={draftSelectedMarkets}
            setSelectedMarkets={setDraftSelectedMarkets}
            selectedBookmakers={draftSelectedBookmakers}
            setSelectedBookmakers={setDraftSelectedBookmakers}
            selectedGameStatus={draftSelectedGameStatus}
            setSelectedGameStatus={setDraftSelectedGameStatus}
            selectedPlayerPropMarkets={draftSelectedPlayerPropMarkets}
            setSelectedPlayerPropMarkets={setDraftSelectedPlayerPropMarkets}
            minProfit={draftMinProfit}
            setMinProfit={setDraftMinProfit}
            maxProfit={draftMaxProfit}
            setMaxProfit={setDraftMaxProfit}
            isEVMode={false}
            userTier={userTier}
          />
        )}
        {activeTab === 'ev' && (
          <FiltersPanel
            isOpen={filtersPanelOpen && filtersPanelMode === 'ev'}
            onCancel={handleCancelFilters}
            onApply={handleApplyFilters}
            sports={sports}
            selectedSports={draftEvSelectedSports}
            setSelectedSports={setDraftEvSelectedSports}
            selectedRegions={[]}
            setSelectedRegions={() => { }}
            selectedMarkets={draftEvSelectedMarkets}
            setSelectedMarkets={setDraftEvSelectedMarkets}
            selectedBookmakers={draftEvSelectedBookmakers}
            setSelectedBookmakers={setDraftEvSelectedBookmakers}
            selectedGameStatus={draftEvSelectedGameStatus}
            setSelectedGameStatus={setDraftEvSelectedGameStatus}
            selectedPlayerPropMarkets={draftEvSelectedPlayerPropMarkets}
            setSelectedPlayerPropMarkets={setDraftEvSelectedPlayerPropMarkets}
            minProfit={draftEvMinEV}
            setMinProfit={setDraftEvMinEV}
            maxProfit={draftEvMaxEV}
            setMaxProfit={setDraftEvMaxEV}
            minProbability={draftEvMinProbability}
            setMinProbability={setDraftEvMinProbability}
            maxProbability={draftEvMaxProbability}
            setMaxProbability={setDraftEvMaxProbability}
            isEVMode={true}
            userTier={userTier}
          />
        )}

        <div className="container">
          {/* Loading State for Subscription */}
          {userTier === null && !profileLoadError && (
            <div className="loading-container" style={{ padding: '40px', textAlign: 'center' }}>
              <div className="spinner"></div>
              <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading subscription details...</p>
            </div>
          )}

          {/* Error State for Subscription */}
          {/* Don't show timeout errors since they resolve themselves */}
          {(profileLoadError || (contextProfileError && !contextProfileError.message?.toLowerCase().includes('timeout'))) && (
            <div className="error-container" style={{ padding: '20px', margin: '20px 0', background: 'rgba(255, 59, 48, 0.1)', border: '1px solid var(--danger)', borderRadius: '8px', color: 'var(--danger)' }}>
              <p>{contextProfileError && !contextProfileError.message?.toLowerCase().includes('timeout') ? `Error loading profile: ${contextProfileError.message}` : profileLoadError}</p>
              <button
                onClick={() => window.location.reload()}
                style={{ marginTop: '12px', padding: '8px 16px', background: 'var(--danger)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Refresh Page
              </button>
            </div>
          )}

          {userTier !== null && (
            <>
              {isOutOfCredits && (
                <div className="credits-alert">
                  <div className="credits-alert-copy">
                    <strong>You&apos;re out of credits</strong>
                    <span>
                      {nextResetMessage
                        ? `Weekly free credits reset ${nextResetMessage}.`
                        : 'Weekly free credits reset every 7 days.'}
                    </span>
                  </div>
                  <Link to="/account/billing" className="credits-alert-link">
                    Open Billing
                  </Link>
                </div>
              )}
              {isProfileStale && userTier === 'free' && (
                <div className="credits-alert" style={{ background: 'rgba(255, 193, 7, 0.15)', borderColor: 'rgba(255, 193, 7, 0.4)' }}>
                  <div className="credits-alert-copy">
                    <strong>⚠️ Subscription data may be outdated</strong>
                    <span>If you recently upgraded your subscription, please refresh the page to see your updated tier.</span>
                  </div>
                  <button
                    onClick={() => window.location.reload()}
                    className="credits-alert-link"
                    style={{ background: 'var(--primary)', color: 'white', border: 'none' }}
                  >
                    Refresh Page
                  </button>
                </div>
              )}
              {activeTab === 'game-arbitrage' ? (
                <>
                  {/* Arbitrage Header */}
                  <div className="ev-header">
                    <h2>Arbitrage</h2>
                    <div className="header-controls">
                      <button className="refresh-btn" onClick={handleManualRefresh} disabled={loading}>
                        <svg
                          className={loading ? 'refresh-icon spinning' : 'refresh-icon'}
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="23 4 23 10 17 10"></polyline>
                          <polyline points="1 20 1 14 7 14"></polyline>
                          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                        </svg>
                        {loading ? 'Refreshing...' : 'Refresh'}
                      </button>
                      <div className="auto-refresh-control" ref={autoRefreshDropdownRef}>
                        <label
                          className="auto-refresh-toggle"
                          onClick={(e) => {
                            if (userTier !== 'platinum') {
                              e.preventDefault();
                              setShowAutoRefreshUpgrade(!showAutoRefreshUpgrade);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={autoRefreshEnabled}
                            onChange={(e) => {
                              if (userTier === 'platinum') {
                                setAutoRefreshEnabled(e.target.checked);
                              }
                            }}
                            disabled={userTier !== 'platinum'}
                          />
                          <span>
                            Auto-refresh {autoRefreshEnabled && userTier === 'platinum' && hasManuallySearched && `(${secondsUntilRefresh}s)`}
                          </span>
                        </label>
                        {showAutoRefreshUpgrade && userTier !== 'platinum' && (
                          <div className="auto-refresh-upgrade-dropdown">
                            <p>Auto-refresh is only available for Platinum members</p>
                            <a href="/account/billing" className="auto-refresh-upgrade-btn">
                              Upgrade to Platinum
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="odds-format-toggle">
                        <button
                          className={`odds-format-btn ${oddsFormat === 'american' ? 'active' : ''}`}
                          onClick={() => setOddsFormat('american')}
                        >
                          American
                        </button>
                        <button
                          className={`odds-format-btn ${oddsFormat === 'decimal' ? 'active' : ''}`}
                          onClick={() => setOddsFormat('decimal')}
                        >
                          Decimal
                        </button>
                      </div>
                      <div className="bet-size-input">
                        <label htmlFor="maxBetSize">Total bet:</label>
                        <input
                          id="maxBetSize"
                          type="number"
                          value={maxBetSize}
                          onChange={(e) => setMaxBetSize(Number(e.target.value))}
                          min="1"
                          step="10"
                        />
                      </div>
                      <label className="round-toggle">
                        <input
                          type="checkbox"
                          checked={roundBets}
                          onChange={(e) => setRoundBets(e.target.checked)}
                        />
                        <span>Round bets</span>
                      </label>
                    </div>
                  </div>

                  {/* Desktop Inline Filters */}
                  <InlineFilters
                    sports={sports}
                    selectedSports={draftSelectedSports}
                    setSelectedSports={setDraftSelectedSports}
                    selectedBookmakers={draftSelectedBookmakers}
                    setSelectedBookmakers={setDraftSelectedBookmakers}
                    selectedGameStatus={draftSelectedGameStatus}
                    setSelectedGameStatus={setDraftSelectedGameStatus}
                    selectedMarkets={draftSelectedMarkets}
                    setSelectedMarkets={setDraftSelectedMarkets}
                    selectedPlayerPropMarkets={draftSelectedPlayerPropMarkets}
                    setSelectedPlayerPropMarkets={setDraftSelectedPlayerPropMarkets}
                    minProfit={draftMinProfit}
                    setMinProfit={setDraftMinProfit}
                    maxProfit={draftMaxProfit}
                    setMaxProfit={setDraftMaxProfit}
                    userTier={userTier}
                    onApplyFilters={handleApplyFilters}
                  />

                  {/* Mobile filters display */}
                  <div className="mobile-filter-summary">
                    <div className="current-filters">
                      <div className="current-filters-text">
                        <div>
                          <span className="filter-label">Filters:</span> {getActiveFiltersText()}
                        </div>
                      </div>
                      <button className="filter-icon-btn" onClick={() => openFiltersPanel('game-arbitrage')}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                        </svg>
                        Filters
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="error">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', marginRight: '8px' }}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      {error}
                      <button onClick={handleManualRefresh} style={{ marginLeft: '12px', padding: '4px 8px', background: 'rgba(255,255,255,0.1)', border: '1px solid var(--danger)', borderRadius: '4px', color: 'var(--danger)', cursor: 'pointer', fontSize: '10px', fontWeight: 600 }}>
                        Retry
                      </button>
                    </div>
                  )}

                  {loading && opportunities.length === 0 ? (
                    <div className="skeleton-table">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="skeleton-row">
                          <div className="skeleton skeleton-cell skeleton-cell-small"></div>
                          <div className="skeleton skeleton-cell skeleton-cell-medium"></div>
                          <div className="skeleton skeleton-cell skeleton-cell-large"></div>
                          <div className="skeleton skeleton-cell skeleton-cell-medium"></div>
                          <div className="skeleton skeleton-cell skeleton-cell-small"></div>
                        </div>
                      ))}
                    </div>
                  ) : filteredOpportunities.length > 0 ? (
                    <OpportunityTable
                      opportunities={filteredOpportunities}
                      maxBetSize={maxBetSize}
                      roundBets={roundBets}
                      calculateBetAmounts={calculateBetAmounts}
                      oddsFormat={oddsFormat}
                      userTier={userTier}
                    />
                  ) : opportunities.length > 0 && searchTerm ? (
                    <div className="empty-state">
                      <div className="empty-state-icon">🔍</div>
                      <div>No matches found</div>
                      <div>Try adjusting your search term or filters</div>
                    </div>
                  ) : !hasManuallySearched ? (
                    <div className="empty-state empty-state--minimal">
                      <button className="empty-state-btn primary empty-state-btn-large" onClick={handleManualRefresh}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="empty-state-btn-icon"
                          aria-hidden="true"
                        >
                          <circle cx="11" cy="11" r="5"></circle>
                          <line x1="16" y1="16" x2="21" y2="21"></line>
                        </svg>
                        <span>Start Search</span>
                      </button>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-state-icon">📊</div>
                      <div>No arbitrage opportunities found</div>
                      <div>Adjust your filters and search criteria to find opportunities</div>
                      <div className="empty-state-cta">
                        <button className="empty-state-btn" onClick={() => openFiltersPanel('game-arbitrage')}>
                          Open Filters
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : activeTab === 'ev' ? (
                <EVOpportunities
                  sports={sports}
                  selectedSports={evSelectedSports}
                  selectedMarkets={evSelectedMarkets}
                  selectedBookmakers={evSelectedBookmakers}
                  selectedPlayerPropMarkets={evSelectedPlayerPropMarkets}
                  minEV={evMinEV}
                  maxEV={evMaxEV}
                  minProbability={evMinProbability}
                  maxProbability={evMaxProbability}
                  searchTerm={evSearchTerm}
                  selectedGameStatus={evSelectedGameStatus}
                  onOpenFilters={() => openFiltersPanel('ev')}
                  hasManuallySearched={evHasManuallySearched}
                  setHasManuallySearched={setEvHasManuallySearched}
                  oddsFormat={oddsFormat}
                  setOddsFormat={setOddsFormat}
                  userTier={userTier}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
